import React, { useState, useEffect, useContext, useCallback, useRef } from 'react';
import { Link, useNavigate, useLocation, Outlet } from 'react-router-dom';
import axios from 'axios';
import AuthContext from '../context/AuthContext';
import { ThemeContext } from '../context/ThemeContext';
import { useSettings } from '../context/SettingsContext';
import BobChatWidget from '../components/BobChatWidget';
import AdminOnboardingTour from '../components/AdminOnboardingTour';

import {
  DashboardIcon as LayoutDashboard,
  UserIcon as User,
  TeamIcon as Users,
  CalendarIcon as Calendar,
  BlogIcon as FileText,
  NotificationIcon as Bell,
  MessageIcon as MessageSquare,
  MenuIcon,
  CloseIcon as X,
  SettingsIcon as Settings,
  AddUserIcon as UserPlus,
  ClipboardIcon as ClipboardList,
  GlobeIcon as Globe,
  LogoutIcon as LogOut,
  ChevronRightIcon as ChevronRight,
  ChevronLeftIcon as ChevronLeft,
  SearchIcon,
  AlertCircleIcon as AlertCircle,
  DarkModeIcon as Moon,
  LightModeIcon as Sun,
  InboxIcon,
  SparklesIcon,
  CheckboxIcon as TaskIcon,
  DollarIcon,
  InventoryIcon,
  KeyIcon,
  ActivityIcon,
  EmailIcon,
  InstagramIcon,
} from '../components/Icons';

const DashboardLayout = () => {
  const { user, logout, API_URL, MAIN_URL } = useContext(AuthContext);
  const { mode, toggleTheme } = useContext(ThemeContext);
  const { settings } = useSettings();
  const modules = settings?.modules || {};
  const navigate = useNavigate();
  const location = useLocation();
  const [profile, setProfile] = useState(null);
  const [loadingProfile, setLoadingProfile] = useState(true);
  const [error, setError] = useState(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const unreadIntervalRef = useRef(null);

  const [isCollapsed, setIsCollapsed] = useState(() => {
    return localStorage.getItem('sidebar-collapsed') === 'true';
  });
  const [searchQuery, setSearchQuery] = useState('');

  const handleToggleCollapse = () => {
    setIsCollapsed(prev => {
      const next = !prev;
      localStorage.setItem('sidebar-collapsed', String(next));
      return next;
    });
  };

  const fetchProfile = useCallback(async () => {
    if (!user) return;
    try {
      const config = {
        headers: {
          Authorization: `Bearer ${user.token}`,
        },
      };
      const { data } = await axios.get(`${API_URL}/users/profile`, config);
      setProfile(data);
      setError(null);
      setLoadingProfile(false);
    } catch (error) {
      console.error('Fehler beim Laden des Profils:', error.response ? error.response.data.message : error.message);

      if (error.response && error.response.status === 401) {
        logout();
        navigate('/login');
        return;
      }

      setError('Das Profil konnte nicht geladen werden. Bitte versuchen Sie es später erneut.');
      setLoadingProfile(false);
    }
  }, [user, API_URL, logout, navigate]);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    fetchProfile();
  }, [user, navigate, fetchProfile]);

  useEffect(() => {
    setIsSidebarOpen(false);
  }, [location]);

  // Fetch unread notification count
  const fetchUnreadCount = useCallback(async () => {
    if (!user?.token) return;
    try {
      const config = { headers: { Authorization: `Bearer ${user.token}` } };
      const { data } = await axios.get(`${API_URL}/notifications/unread-count`, config);
      setUnreadCount(data.count);
    } catch (err) {
      if (err.response?.status === 404) {
        console.warn('Dashboard: Notifications unread-count route not found.');
      } else {
        console.error('Error fetching unread count:', err);
      }
    }
  }, [user, API_URL]);



  useEffect(() => {
    if (user) {
      fetchUnreadCount();
      // Poll every 60 seconds
      unreadIntervalRef.current = setInterval(() => {
        fetchUnreadCount();
      }, 60000);
    }
    return () => clearInterval(unreadIntervalRef.current);
  }, [user, fetchUnreadCount]);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const getNavSections = () => {
    const hasPerm = (perm) => {
      const currentRole = profile?.role || user?.role;
      if (currentRole === 'admin') return true;
      if (profile?.customPermissions?.includes(perm)) return true;

      // Role defaults
      if (currentRole === 'gruppenleiter') {
        const glPerms = ['blog.create', 'blog.edit', 'blog.publish', 'event.create', 'event.edit', 'user.view'];
        if (glPerms.includes(perm)) return true;
      }
      if (currentRole === 'eltern') {
        if (perm === 'child.manage') return true;
      }
      if (currentRole === 'kind') {
        if (perm === 'blog.submit') return true;
      }
      return false;
    };

    const currentRole = profile?.role || user?.role || 'kind';
    const targetMainUrl = MAIN_URL || (typeof window !== 'undefined' ? (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1' ? 'http://localhost:3000' : `${window.location.protocol}//${window.location.hostname.replace(/^dash\./, '')}`) : '/');
    const isAiEnabled = (settings?.ai?.enabled ?? settings?.integrations?.ai?.enabled) !== false &&
      modules.aiAssistant !== false &&
      settings?.features?.bobChat !== false &&
      settings?.features?.aiAssistant !== false &&
      settings?.ai_settings?.enabled !== false;
    const isCloudflareEnabled = (settings?.cloudflare?.enabled ?? settings?.integrations?.cloudflare?.enabled) !== false && modules.mailbox !== false;
    const isAnalyticsEnabled = (settings?.analytics?.enabled ?? settings?.integrations?.analytics?.enabled) !== false && modules.analytics !== false;

    const generalItems = [
      { path: targetMainUrl, label: 'Zurück zur Website', icon: <Globe style={{ fontSize: 20 }} />, external: true },
      { path: '/', label: 'Übersicht', icon: <LayoutDashboard style={{ fontSize: 20 }} /> },
    ];
    if (modules.mailbox !== false) {
      generalItems.push({ path: '/inbox', label: 'Postfach', icon: <InboxIcon style={{ fontSize: 20 }} />, badge: unreadCount });
    }
    if (isAiEnabled) {
      generalItems.push({ path: '/ai-agent', label: 'Bob', icon: <SparklesIcon style={{ fontSize: 20 }} /> });
    }
    generalItems.push({ path: '/profile', label: 'Mein Account', icon: <User style={{ fontSize: 20 }} /> });
    generalItems.push({ path: '/notifications', label: 'Mitteilungen', icon: <Bell style={{ fontSize: 20 }} /> });

    const sections = [
      {
        title: 'Allgemein',
        items: generalItems
      }
    ];

    // --- Familie Section (Eltern only) ---
    if (currentRole === 'eltern') {
      sections.push({
        title: 'Familie',
        items: [
          { path: '/my-children', label: 'Meine Kinder', icon: <Users style={{ fontSize: 20 }} /> },
        ]
      });
    }

    // --- Team-Verwaltung (GL/Admin only) ---
    const teamItems = [];
    if (modules.team !== false && (hasPerm('event.create') || currentRole === 'gruppenleiter')) {
      teamItems.push({ path: '/my-team-page', label: 'Meine Seite', icon: <Globe style={{ fontSize: 20 }} /> });
    }
    if (modules.groups !== false && (currentRole === 'admin' || currentRole === 'gruppenleiter')) {
      teamItems.push({ path: '/groups', label: 'Gruppen & Stufen', icon: <Users style={{ fontSize: 20 }} /> });
    }
    if (modules.events !== false && (hasPerm('event.create') || hasPerm('event.edit'))) {
      teamItems.push({ path: '/events', label: 'Events', icon: <Calendar style={{ fontSize: 20 }} /> });
    }
    if (modules.blog !== false && (hasPerm('blog.create') || hasPerm('blog.publish') || currentRole === 'admin' || currentRole === 'gruppenleiter')) {
      teamItems.push({ path: '/blog', label: 'Blog', icon: <FileText style={{ fontSize: 20 }} /> });
      teamItems.push({ path: '/instagram-generator', label: 'Instagram Generator', icon: <InstagramIcon style={{ fontSize: 20 }} /> });
    }
    if (modules.contactForm !== false && (hasPerm('user.view') || currentRole === 'admin' || currentRole === 'gruppenleiter')) {
      teamItems.push({ path: '/contact-requests', label: 'Kontaktanfragen', icon: <MessageSquare style={{ fontSize: 20 }} /> });
    }
    if (modules.tasks !== false && (currentRole === 'admin' || currentRole === 'gruppenleiter')) {
      teamItems.push({ path: '/tasks', label: 'Aufgaben', icon: <TaskIcon style={{ fontSize: 20 }} /> });
    }
    if (modules.attendance !== false && (currentRole === 'admin' || currentRole === 'gruppenleiter')) {
      teamItems.push({ path: '/attendance', label: 'Anwesenheit', icon: <ClipboardList style={{ fontSize: 20 }} /> });
    }
    if (modules.mailbox !== false && (currentRole === 'admin' || currentRole === 'gruppenleiter')) {
      teamItems.push({ path: '/mailbox', label: 'Mailbox', icon: <InboxIcon style={{ fontSize: 20 }} /> });
    }

    if (teamItems.length > 0) {
      sections.push({
        title: 'Team-Verwaltung',
        items: teamItems
      });
    }

    // --- Warte & Fachbereiche (Kassenwart, Materialwart & Admin/GL) ---
    const warte = Array.isArray(user?.userData?.warte)
      ? user.userData.warte
      : (user?.userData?.warte ? [user.userData.warte] : []);
    const isKassenwart = warte.includes('kassenwart') || hasPerm('finance.manage') || currentRole === 'admin';
    const isMaterialwart = warte.includes('materialwart') || hasPerm('inventory.manage') || currentRole === 'admin' || currentRole === 'gruppenleiter';

    const warteItems = [];
    if (modules.finances !== false && isKassenwart) {
      warteItems.push({ path: '/finances', label: 'Finanzen & Kasse', icon: <DollarIcon style={{ fontSize: 20 }} /> });
    }
    if (modules.inventory !== false && isMaterialwart) {
      warteItems.push({ path: '/inventory', label: 'Material & Inventar', icon: <InventoryIcon style={{ fontSize: 20 }} /> });
    }

    if (warteItems.length > 0) {
      sections.push({
        title: 'Warte & Verwaltung',
        items: warteItems
      });
    }

    // --- Mein Bereich for Kinder (Blog schreiben) ---
    if (currentRole === 'kind' && modules.blog !== false) {
      sections.push({
        title: 'Mein Bereich',
        items: [
          { path: '/blog', label: 'Blog schreiben', icon: <FileText style={{ fontSize: 20 }} /> },
        ]
      });
    }

    // --- Administration (Admin only) ---
    const adminItems = [];
    if (hasPerm('user.manage') || currentRole === 'admin') {
      adminItems.push({ path: '/users', label: 'Benutzer', icon: <Users style={{ fontSize: 20 }} /> });
      adminItems.push({ path: '/pending-registrations', label: 'Registrierungen', icon: <UserPlus style={{ fontSize: 20 }} /> });
    }
    if (currentRole === 'admin') {
      adminItems.push({ path: '/admin-tools', label: 'Admin-Tools', icon: <Settings style={{ fontSize: 20 }} /> });
    }

    if (adminItems.length > 0) {
      sections.push({
        title: 'Administration',
        items: adminItems
      });
    }

    // --- Schnittstellen & APIs (Admin only) ---
    if (currentRole === 'admin') {
      const schnittstellenItems = [
        { path: '/admin-tools/integrations', label: 'Übersicht & Keys', icon: <KeyIcon style={{ fontSize: 20 }} /> },
      ];
      if (isAiEnabled) {
        schnittstellenItems.push({ path: '/admin-tools/bob', label: 'Bob KI-Assistent', icon: <SparklesIcon style={{ fontSize: 20 }} /> });
      }
      if (isAnalyticsEnabled) {
        schnittstellenItems.push({ path: '/admin-tools/analytics', label: 'Google Analytics', icon: <ActivityIcon style={{ fontSize: 20 }} /> });
      }
      if (isCloudflareEnabled) {
        schnittstellenItems.push({ path: '/admin-tools/emails', label: 'E-Mail-Routing', icon: <EmailIcon style={{ fontSize: 20 }} /> });
      }
      schnittstellenItems.push({ path: '/admin-tools/llms', label: 'Robots & LLMs', icon: <Globe style={{ fontSize: 20 }} /> });

      sections.push({
        title: 'Schnittstellen',
        items: schnittstellenItems
      });
    }

    // --- Pfadi-Alltag (everyone) ---
    const pfadiItems = [];
    if (modules.events !== false) {
      pfadiItems.push({ path: '/my-registrations', label: 'Anmeldungen', icon: <ClipboardList style={{ fontSize: 20 }} /> });
    }

    if (pfadiItems.length > 0) {
      sections.push({ title: 'Pfadi-Alltag', items: pfadiItems });
    }

    return sections;
  };

  const sidebarScrollRef = useRef(null);

  const renderSidebarContent = () => {
    const sections = getNavSections();

    // Filter sections based on search query
    const filteredSections = sections.map(section => {
      const filteredItems = section.items.filter(item =>
        item.label.toLowerCase().includes(searchQuery.toLowerCase())
      );
      return { ...section, items: filteredItems };
    }).filter(section => section.items.length > 0);

    const isBeta = settings?.isBeta || (typeof window !== 'undefined' && (window.location.hostname.includes('beta') || window.location.hostname.includes('localhost')));

    return (
      <div className={`flex flex-col h-full ${isCollapsed ? 'p-3' : 'p-4 md:p-6'} transition-all`}>
        {/* Sidebar Header with Dashboard title and Collapse Toggle */}
        <div className="flex items-center justify-between mb-4">
          {!isCollapsed && (
            <div className="flex items-center gap-2 pl-2">
              <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center text-primary">
                <LayoutDashboard style={{ fontSize: 18 }} />
              </div>
              <span className="font-bold text-surface-on">Dashboard</span>
            </div>
          )}
          <button
            onClick={handleToggleCollapse}
            title={isCollapsed ? "Sidebar ausklappen" : "Sidebar einklappen"}
            className={`hidden md:flex w-8 h-8 rounded-full items-center justify-center text-surface-onVariant hover:bg-surface-on/5 transition-all ${isCollapsed ? 'mx-auto' : 'ml-auto'}`}
          >
            {isCollapsed ? <ChevronRight style={{ fontSize: 20 }} /> : <ChevronLeft style={{ fontSize: 20 }} />}
          </button>
        </div>

        {/* Beta Staging Indicator Badge */}
        {isBeta && (
          <div
            className={`mb-4 px-3 py-1.5 rounded-xl bg-amber-500/10 border border-amber-500/25 text-amber-600 dark:text-amber-400 text-xs font-bold flex items-center ${isCollapsed ? 'justify-center' : 'gap-2'}`}
            title="White-Label Beta-Testumgebung: Änderungen betreffen nur diesen Teststamm"
          >
            <span>🧪</span>
            {!isCollapsed && <span>Beta Staging</span>}
          </div>
        )}

        {/* Dynamic Search Bar (Only shown when expanded) */}
        {!isCollapsed && (
          <div className="mb-6 relative mx-2">
            <input
              type="text"
              placeholder="Menü filtern..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full bg-surface-containerHighest text-surface-on px-4 py-2 pl-9 rounded-2xl text-[14px] border border-outlineVariant/30 focus:border-primary/50 focus:outline-none transition-colors"
            />
            <SearchIcon className="absolute left-3 top-2.5 text-surface-onVariant/60" style={{ fontSize: 16 }} />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-2.5 text-surface-onVariant/60 hover:text-surface-on"
              >
                <X style={{ fontSize: 16 }} />
              </button>
            )}
          </div>
        )}

        <div ref={sidebarScrollRef} className="flex-1 overflow-y-auto pr-2 custom-scrollbar">
          {filteredSections.map((section) => (
            <div key={section.title} className={isCollapsed ? "mb-4" : "mb-6 md:mb-8"}>
              {!isCollapsed ? (
                <h3 className="px-4 mb-2 md:mb-3 text-[10px] font-bold uppercase tracking-[0.2em] text-surface-onVariant opacity-60">
                  {section.title}
                </h3>
              ) : (
                <div className="border-t border-outlineVariant/20 my-2 mx-1" />
              )}
              <div className="space-y-1">
                {section.items.map((item) => {
                  const isActive = !item.external && ((item.path === '/' && (location.pathname === '/' || location.pathname === '')) ||
                    (item.path !== '/' && location.pathname.startsWith(item.path)));

                  const content = (
                    <div className={`flex items-center ${isCollapsed ? 'justify-center w-full' : 'gap-3'}`}>
                      <div className={`transition-transform duration-medium2 group-hover:scale-110 ${isActive ? 'text-secondary font-bold' : 'opacity-80'}`}>
                        {item.icon}
                      </div>
                      {!isCollapsed && (
                        <span className={`text-[15px] ${isActive ? 'font-bold' : 'font-medium'}`}>
                          {item.label}
                        </span>
                      )}
                    </div>
                  );

                  if (item.external) {
                    return (
                      <a
                        key={item.path}
                        href={item.path}
                        data-tour={`nav-${item.path.replace(/^\//, '') || 'home'}`}
                        title={isCollapsed ? item.label : ''}
                        className={`group flex items-center ${isCollapsed ? 'justify-center p-3' : 'justify-between px-4 py-3'} rounded-2xl transition-all duration-medium2 hover:bg-surface-on/[0.06] text-surface-onVariant dark:text-surface-on`}
                      >
                        {content}
                      </a>
                    );
                  }

                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      data-tour={`nav-${item.path.replace(/^\//, '') || 'home'}`}
                      title={isCollapsed ? item.label : ''}
                      className={`group flex items-center ${isCollapsed ? 'justify-center relative p-3' : 'justify-between px-4 py-3'} rounded-2xl transition-all duration-medium2 ${isActive
                        ? 'bg-secondary-container text-secondary-onContainer shadow-sm'
                        : 'hover:bg-surface-on/[0.06] text-surface-onVariant dark:text-surface-on'
                        }`}
                    >
                      {content}
                      {!isCollapsed && isActive && <ChevronRight style={{ fontSize: 14 }} className="opacity-40" />}
                      {item.badge > 0 && (
                        isCollapsed ? (
                          <span className="absolute top-1 right-1 w-2.5 h-2.5 rounded-full bg-error animate-pulse shadow-sm" />
                        ) : (
                          <span className="ml-auto px-2 py-0.5 rounded-full text-[10px] font-black bg-error text-error-on min-w-[20px] text-center animate-scale-in shadow-sm">
                            {item.badge > 99 ? '99+' : item.badge}
                          </span>
                        )
                      )}
                    </Link>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        <div className="mt-4 pt-4 border-t border-outlineVariant/30 space-y-2">
          <button
            onClick={toggleTheme}
            title={isCollapsed ? "Theme umschalten" : "Theme wechseln"}
            className={`w-full flex items-center ${isCollapsed ? 'justify-center p-3' : 'justify-between px-4 py-3'} rounded-2xl transition-all duration-medium1 group ${mode === 'dark'
                ? 'bg-amber-500/10 text-amber-500 hover:bg-amber-500/20'
                : 'text-surface-onVariant hover:bg-surface-on/5'
              }`}
          >
            <div className="flex items-center gap-3">
              {mode === 'dark' ? <Sun style={{ fontSize: 20 }} /> : <Moon style={{ fontSize: 20 }} />}
              {!isCollapsed && <span className="font-medium">Dark Mode</span>}
            </div>
            {!isCollapsed && (
              <div className={`w-10 h-6 rounded-full p-1 transition-colors ${mode === 'dark' ? 'bg-amber-500' : 'bg-surface-onVariant/30'}`}>
                <div className={`w-4 h-4 rounded-full bg-white transition-transform ${mode === 'dark' ? 'translate-x-4' : 'translate-x-0'}`} />
              </div>
            )}
          </button>

          <button
            onClick={handleLogout}
            title={isCollapsed ? "Abmelden" : "Konto abmelden"}
            className={`w-full flex items-center ${isCollapsed ? 'justify-center p-3' : 'gap-3 px-4 py-3'} rounded-2xl text-error font-bold hover:bg-error/10 transition-all duration-medium1 group`}
          >
            <LogOut style={{ fontSize: 20 }} className={`${!isCollapsed ? 'group-hover:-translate-x-1' : ''} transition-transform`} />
            {!isCollapsed && <span>Abmelden</span>}
          </button>
        </div>
      </div>
    );
  };

  if (error) {
    return (
      <div className="min-h-screen bg-surface flex flex-col items-center justify-center p-6 text-center">
        <div className="w-16 h-16 bg-error/10 text-error rounded-full flex items-center justify-center mb-6">
          <AlertCircle size={32} />
        </div>
        <h1 className="text-display-small font-bold text-surface-on mb-2">Ladefehler</h1>
        <p className="text-body-large text-surface-onVariant mb-8">{error}</p>
        <div className="flex gap-4">
          <button onClick={() => { setError(null); setLoadingProfile(true); fetchProfile(); }} className="md3-btn-filled">
            Erneut versuchen
          </button>
          <button onClick={handleLogout} className="md3-btn-text text-error">
            Abmelden
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-[100dvh] bg-surface flex flex-col overflow-hidden">
      <div className="md:hidden flex items-center justify-between px-6 py-4 bg-surface-containerLow border-b border-outlineVariant/50">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center text-primary">
            <LayoutDashboard style={{ fontSize: 18 }} />
          </div>
          <span className="font-bold text-surface-on">Dashboard</span>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={toggleTheme}
            className={`w-10 h-10 rounded-full flex items-center justify-center transition-all ${mode === 'dark' ? 'bg-amber-500/10 text-amber-500' : 'text-surface-onVariant hover:bg-surface-on/5'
              }`}
          >
            {mode === 'dark' ? <Sun style={{ fontSize: 20 }} /> : <Moon style={{ fontSize: 20 }} />}
          </button>
          <button
            onClick={() => setIsSidebarOpen(!isSidebarOpen)}
            className="w-10 h-10 rounded-full flex items-center justify-center text-surface-onVariant hover:bg-surface-on/5"
          >
            {isSidebarOpen ? <X style={{ fontSize: 24 }} /> : <MenuIcon sx={{ fontSize: 24 }} />}
          </button>
        </div>
      </div>

      <div className="flex flex-1 overflow-hidden relative">
        <aside className={`hidden md:flex flex-col ${isCollapsed ? 'w-20' : 'w-72'} bg-surface-containerLow rounded-r-[2rem] border-r border-outlineVariant/10 py-6 transition-all duration-300 ease-in-out h-full overflow-hidden shrink-0`}>
          {renderSidebarContent()}
        </aside>

        <div
          className={`md:hidden fixed inset-0 z-40 bg-black/40 backdrop-blur-sm transition-opacity duration-300 ${isSidebarOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
          onClick={() => setIsSidebarOpen(false)}
        />

        <aside
          className={`md:hidden fixed inset-y-0 right-0 z-50 w-[80%] max-w-[300px] bg-surface-containerLow shadow-elevation-4 transition-transform duration-emphasized ease-emphasized rounded-l-[2rem] ${isSidebarOpen ? 'translate-x-0' : 'translate-x-full'}`}
        >
          {renderSidebarContent()}
        </aside>

        <main className="flex-1 overflow-y-auto bg-surface relative flex flex-col min-h-0 custom-scrollbar [scrollbar-gutter:stable]">
          <div className="flex-1 flex flex-col min-h-0">
            <Outlet context={{ profile, refreshProfile: fetchProfile, loadingProfile }} />
          </div>

        </main>
      </div>
      {modules.aiAssistant !== false &&
        settings?.features?.bobChat !== false &&
        settings?.features?.aiAssistant !== false &&
        settings?.ai?.enabled !== false &&
        settings?.ai_settings?.enabled !== false && (
          <BobChatWidget />
        )}
      {user?.role === 'admin' && <AdminOnboardingTour />}
    </div>
  );
};

export default DashboardLayout;
